import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { AnimatedScrollable } from '@sorare/core/src/atoms/navigation/AnimatedScrollable';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';

import { DailyDealsTitle } from '../DailyDeals/DailyDealsTitle';
import { PackWithBuyButton } from '../PackWithBuyButton';
import type { CompactDailyDeals_cardPackGroup } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  overflow: hidden;
  padding: var(--double-unit);
  gap: var(--double-unit);
`;

type Props = {
  cardPackGroup: CompactDailyDeals_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, cardPackSlug: string) => void;
};
export const CompactDailyDeals = ({ cardPackGroup, onClickBuy }: Props) => {
  const { cardPacks } = cardPackGroup;

  const items = cardPacks.map(pack => {
    return {
      key: pack.slug,
      pictureUrl: proxyUrl(pack.pictureUrl, {
        cropWidth: 160,
      }),
    };
  });
  const [selectedItemKey, setSelectedItemKey] = useState(
    cardPacks.at(0)?.slug || ''
  );

  return (
    <Root gap={0}>
      <DailyDealsTitle cardPackGroup={cardPackGroup} />
      <AnimatedScrollable
        items={items}
        selectedItemKey={selectedItemKey}
        onScrollToItem={setSelectedItemKey}
        itemWidth={144}
        withShadow={false}
      >
        {({ key }) => {
          const cardPack = cardPacks.find(pack => pack.slug === key);
          if (!cardPack) {
            return null;
          }
          return (
            <PackWithBuyButton
              key={key}
              cardPack={cardPack}
              cardPackGroupId={cardPackGroup.id}
              onClickBuy={onClickBuy}
              gap={3}
            />
          );
        }}
      </AnimatedScrollable>
    </Root>
  );
};

CompactDailyDeals.fragments = {
  cardPackGroup: gql`
    fragment CompactDailyDeals_cardPackGroup on CardPackGroup {
      id
      cardPacks {
        slug
        ...PackWithBuyButton_cardPack
      }
      ...DailyDealsTitle_cardPackGroup
    }
    ${DailyDealsTitle.fragments.cardPackGroup}
    ${PackWithBuyButton.fragments.cardPack}
  ` as TypedDocumentNode<CompactDailyDeals_cardPackGroup>,
};
